import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { apiBaseUrl, getClientHistory, validateCheckIn } from "../lib/api";
import type { ClientHistoryResponse, InitialAssessment, WeeklyCheckIn } from "../types";

type CoachClientTab = "summary" | "progress" | "checkin" | "routine";

type TrackingRow = {
  date: string;
  label: string;
  waterLiters: string;
  sleepHours: string;
  steps: string;
  recoveryScore: string;
  weightKg: string;
  dietType: string;
  trainedMuscle: string;
  trainingPerformance: string;
  cardio: string;
  bloodGlucose: string;
  dayReflections: string;
  bloodPressure: string;
};

const statusLabel: Record<string, string> = {
  NEW: "Nuevo",
  PROGRESS: "Activo",
  STAGNATED: "Pendiente",
  REGRESSION: "Pendiente",
};

function normalizeWeekday(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

function extractTrainingDays(value: string) {
  const weekdayMatchers = [
    { aliases: ["lunes", "lun"], label: "Lunes" },
    { aliases: ["martes", "mar"], label: "Martes" },
    { aliases: ["miércoles", "miércoles", "mie", "mié"], label: "Miércoles" },
    { aliases: ["jueves", "jue"], label: "Jueves" },
    { aliases: ["viernes", "vie"], label: "Viernes" },
    { aliases: ["sábado", "sábado", "sab", "sáb"], label: "Sábado" },
    { aliases: ["domingo", "dom"], label: "Domingo" },
  ];
  const normalizedValue = normalizeWeekday(value);
  const specificSegmentMatch = normalizedValue.match(/días? de entrenamiento\s*:\s*([^.\n]+)/);
  const source = specificSegmentMatch?.[1]?.trim() || normalizedValue;
  return weekdayMatchers
    .filter((item) => item.aliases.some((alias) => source.includes(normalizeWeekday(alias))))
    .map((item) => item.label);
}

function formatWeekdayLabel(value: string) {
  const date = new Date(`${value}T00:00:00`);
  const label = new Intl.DateTimeFormat("es-PE", { weekday: "long" }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatShortDashboardDate(value: string) {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return new Intl.DateTimeFormat("es-PE", { day: "numeric", month: "short" }).format(date).replace(".", "");
}

function generateTrackingRows(weekStart: string, allowedDays: string[] = []): TrackingRow[] {
  const allowed = new Set(allowedDays.map((day) => normalizeWeekday(day)));
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(`${weekStart}T00:00:00`);
    date.setDate(date.getDate() + index);
    const isoDate = date.toISOString().slice(0, 10);
    const label = formatWeekdayLabel(isoDate);
    return {
      date: isoDate,
      label,
      waterLiters: "",
      sleepHours: "",
      steps: "",
      recoveryScore: "",
      weightKg: "",
      dietType: "",
      trainedMuscle: "",
      trainingPerformance: "",
      cardio: "",
      bloodGlucose: "",
      dayReflections: "",
      bloodPressure: "",
    };
  }).filter((entry) => allowed.size === 0 || allowed.has(normalizeWeekday(entry.label)));
}

function extractObservationValue(observations: string, label: string) {
  const normalizedLabel = normalizeWeekday(label);
  const lines = observations.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const matchedLine = lines.find((line) => {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) return false;
    const lineLabel = normalizeWeekday(line.slice(0, separatorIndex));
    return lineLabel === normalizedLabel;
  });
  if (!matchedLine) return "";
  return matchedLine.slice(matchedLine.indexOf(":") + 1).trim();
}

function parseDailyObservationEntries(observations: string) {
  const byDate = new Map<string, Partial<TrackingRow>>();
  const jsonMatch = observations.match(/TRACKING_DIARIO_JSON:\s*(\[[\s\S]*?\])\s*(?:\n\n|$)/);
  if (jsonMatch?.[1]) {
    try {
      const entries = JSON.parse(jsonMatch[1]);
      if (Array.isArray(entries)) {
        entries.forEach((entry) => {
          if (entry?.date) byDate.set(entry.date, entry);
        });
        return byDate;
      }
    } catch {
      // legacy fallback
    }
  }
  const pattern = /^.+\((\d{4}-\d{2}-\d{2})\): peso .*?, pasos (.*?), dieta (.*)$/gim;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(observations)) !== null) {
    byDate.set(match[1], {
      steps: match[2] === "-" ? "" : match[2],
      dietType: match[3] === "-" ? "" : match[3],
    });
  }
  return byDate;
}

function buildTrackingRowsFromCheckIn(checkIn: WeeklyCheckIn, allowedDays: string[] = []) {
  const observations = checkIn.observations || "";
  const dailyObservationEntries = parseDailyObservationEntries(observations);
  const hungerNotes = extractObservationValue(observations, "Hambre");
  const digestionNotes = extractObservationValue(observations, "Digestión");
  const trainingNotes = extractObservationValue(observations, "Evolución del entrenamiento");
  const extraNotes = extractObservationValue(observations, "Observaciones adicionales");
  const repeatedReflection = [hungerNotes, digestionNotes, trainingNotes, extraNotes].filter(Boolean).join(" · ");

  const baseRows = generateTrackingRows(checkIn.weekStart.slice(0, 10), allowedDays);

  return baseRows.map((row) => {
    const observationRow = dailyObservationEntries.get(row.date);
    const savedWeight = checkIn.dailyWeights.find((weight) => weight.date.slice(0, 10) === row.date);
    return {
      ...row,
      waterLiters: observationRow?.waterLiters || (checkIn.waterLiters ? String(checkIn.waterLiters) : row.waterLiters),
      sleepHours: observationRow?.sleepHours || row.sleepHours,
      steps: observationRow?.steps || row.steps,
      recoveryScore: observationRow?.recoveryScore || (checkIn.sleepScore ? String(checkIn.sleepScore) : row.recoveryScore),
      weightKg: savedWeight?.weightKg ? String(savedWeight.weightKg) : row.weightKg,
      dietType: observationRow?.dietType || row.dietType,
      trainedMuscle: observationRow?.trainedMuscle || row.trainedMuscle,
      trainingPerformance: observationRow?.trainingPerformance || row.trainingPerformance,
      cardio: observationRow?.cardio || checkIn.cardioCompleted || row.cardio,
      bloodGlucose: observationRow?.bloodGlucose || row.bloodGlucose,
      dayReflections: observationRow?.dayReflections || repeatedReflection || row.dayReflections,
      bloodPressure: observationRow?.bloodPressure || row.bloodPressure,
    };
  });
}

function averageTrackingField(rows: TrackingRow[], field: keyof TrackingRow) {
  const values = rows
    .map((row) => Number(String(row[field] ?? "").replace(",", ".")))
    .filter((value) => !Number.isNaN(value) && value > 0);
  if (values.length === 0) return "-";
  return (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1);
}

function calculateWeightVariation(rows: TrackingRow[]) {
  const weights = rows
    .map((row) => Number(String(row.weightKg ?? "").replace(",", ".")))
    .filter((value) => !Number.isNaN(value) && value > 0);
  if (weights.length < 2) return "-";
  return (weights[weights.length - 1] - weights[0]).toFixed(1);
}

function formatMuscleTrainedLabel(value?: string | null) {
  const labels: Record<string, string> = {
    CHEST: "Pecho",
    PUSH: "Pecho",
    Push: "Pecho",
    Empuje: "Pecho",
    Pecho: "Pecho",
    BACK: "Espalda",
    PULL: "Espalda",
    Pull: "Espalda",
    "Jalón": "Espalda",
    Jalon: "Espalda",
    Espalda: "Espalda",
    SHOULDERS: "Hombros",
    Hombros: "Hombros",
    BICEPS: "Bíceps",
    Biceps: "Bíceps",
    TRICEPS: "Tríceps",
    Triceps: "Tríceps",
    LEGS: "Piernas",
    Legs: "Piernas",
    Piernas: "Piernas",
    UPPER: "Tren superior",
    Upper: "Tren superior",
    GLUTES: "Glúteos",
    Gluteos: "Glúteos",
    ABS: "Abdomen",
    Abdomen: "Abdomen",
    FULL_BODY: "Cuerpo completo",
    "Full body": "Cuerpo completo",
    "Cuerpo completo": "Cuerpo completo",
    REST: "Descanso",
    "Descanso activo": "Descanso",
    Descanso: "Descanso",
  };
  return value ? labels[value] ?? value : "";
}

function getCheckInWeekNumber(checkIns: WeeklyCheckIn[] = [], checkInId: string) {
  const ordered = [...checkIns].sort((a, b) => {
    const weekComparison = a.weekStart.localeCompare(b.weekStart);
    if (weekComparison !== 0) {
      return weekComparison;
    }

    return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
  });
  const index = ordered.findIndex((item) => item.id === checkInId);
  return index >= 0 ? index + 1 : 1;
}

function sanitizePdfFileNamePart(value: string) {
  const sanitized = value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "");

  return sanitized || "Cliente";
}

function buildCheckInPdfTitle(clientName: string, weekNumber: number) {
  const safeWeekNumber = Math.max(1, weekNumber).toString().padStart(2, "0");
  return `${sanitizePdfFileNamePart(clientName)}_Semana${safeWeekNumber}_JCFitness`;
}

function printCheckInDocument(fileName: string) {
  const previousTitle = document.title;
  let restored = false;
  const restoreTitle = () => {
    if (restored) return;
    restored = true;
    document.title = previousTitle;
    window.removeEventListener("afterprint", restoreTitle);
  };

  document.title = fileName;
  window.addEventListener("afterprint", restoreTitle);
  window.print();
  window.setTimeout(restoreTitle, 15000);
}

function buildSavedCheckInAnswers(checkIn: WeeklyCheckIn) {
  const observations = checkIn.observations || "";
  const answers = [
    ["Sueño", extractObservationValue(observations, "Sueño")],
    ["Hambre", extractObservationValue(observations, "Hambre")],
    ["Ansiedad", extractObservationValue(observations, "Ansiedad")],
    ["Estrés", extractObservationValue(observations, "Estrés")],
    ["Comidas fuera de dieta", extractObservationValue(observations, "Comidas fuera de dieta")],
    ["Entrenamientos completados", extractObservationValue(observations, "Entrenamientos completados")],
    ["Cardios completados", extractObservationValue(observations, "Cardios completados")],
    ["Digestión", extractObservationValue(observations, "Digestión")],
    ["Evolución del entrenamiento", extractObservationValue(observations, "Evolución del entrenamiento")],
    ["Mejoras propuestas", extractObservationValue(observations, "Mejoras propuestas")],
    ["Fase del ciclo menstrual", checkIn.menstrualCyclePhase || ""],
    ["Síntomas menstruales", checkIn.menstrualSymptoms || ""],
    ["¿Qué día vino y se fue el periodo?", checkIn.menstrualPeriodDates || ""],
    ["Observaciones adicionales", extractObservationValue(observations, "Observaciones adicionales")],
  ];
  return answers.filter(([, value]) => value && value !== "-");
}

function buildAssessmentSummaryItems(assessment: InitialAssessment | null) {
  if (!assessment) return [];
  const sections = [
    { id: "gymExperience", title: "Experiencia en el gimnasio", tone: "orange", icon: "dumbbell", value: assessment.gymExperience },
    { id: "currentDiet", title: "Dieta actual", tone: "green", icon: "fork", value: assessment.currentDiet },
    { id: "mealSchedule", title: "Horario de comidas y rutina diaria", tone: "blue", icon: "clock", value: assessment.mealSchedule },
    { id: "supplements", title: "Suplementos y esteroides", tone: "purple", icon: "pill", value: assessment.supplements },
    { id: "trainingRoutine", title: "Rutina de entrenamiento", tone: "red", icon: "arm", value: assessment.trainingRoutine },
    { id: "cardioRoutine", title: "Cardio", tone: "teal", icon: "heart", value: assessment.cardioRoutine },
    { id: "lifestyleNotes", title: "Estilo de vida", tone: "amber", icon: "person", value: assessment.lifestyleNotes },
    { id: "foodPreferences", title: "Preferencias alimenticias", tone: "leaf", icon: "leaf", value: assessment.foodPreferences },
    { id: "healthBackground", title: "Antecedentes de salud", tone: "shield", icon: "shield", value: assessment.healthBackground },
  ];
  return sections.map((section) => ({
    ...section,
    bullets: String(section.value || "")
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => !line.startsWith("¿") && !line.endsWith("?"))
      .slice(0, 6),
  }));
}

function renderAssessmentSummaryIcon(icon: string) {
  if (icon === "fork") return <svg viewBox="0 0 24 24"><path d="M7 3v8" /><path d="M4 3v8" /><path d="M10 3v8" /><path d="M4 11h6" /><path d="M7 11v10" /><path d="M17 3v18" /><path d="M14 7c0-2 1.5-4 3-4" /></svg>;
  if (icon === "dumbbell") return <svg viewBox="0 0 24 24"><path d="M6 8v8M18 8v8M3 10v4M21 10v4M6 12h12" /></svg>;
  if (icon === "clock") return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3 2" /></svg>;
  if (icon === "pill") return <svg viewBox="0 0 24 24"><path d="M10 21 3 14a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7z" /><path d="m7 10 7 7" /></svg>;
  if (icon === "arm") return <svg viewBox="0 0 24 24"><path d="M7 20c4 0 7-2 7-6V8a3 3 0 0 1 6 0v3" /><path d="M7 20c-2-2-3-5-2-8l2-7" /><path d="M11 13h8" /></svg>;
  if (icon === "heart") return <svg viewBox="0 0 24 24"><path d="M20.5 6.5a5 5 0 0 0-7 0L12 8l-1.5-1.5a5 5 0 0 0-7 7L12 22l8.5-8.5a5 5 0 0 0 0-7z" /><path d="M7 12h3l2-3 2 6 2-3h2" /></svg>;
  if (icon === "leaf") return <svg viewBox="0 0 24 24"><path d="M5 19c8 0 14-6 14-14C11 5 5 11 5 19z" /><path d="M5 19 15 9" /></svg>;
  if (icon === "shield") return <svg viewBox="0 0 24 24"><path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" /><path d="m9 12 2 2 4-5" /></svg>;
  if (icon === "target") return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></svg>;
  return <svg viewBox="0 0 24 24"><circle cx="12" cy="7" r="3" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>;
}

function getCheckInAnswerTone(label: string) {
  const normalized = normalizeWeekday(label);
  if (normalized.includes("sueno")) return "blue";
  if (normalized.includes("hambre")) return "orange";
  if (normalized.includes("ansiedad")) return "pink";
  if (normalized.includes("estres")) return "purple";
  if (normalized.includes("comida")) return "amber";
  if (normalized.includes("entrenamientos completados")) return "green";
  if (normalized.includes("cardio")) return "red";
  if (normalized.includes("agua")) return "sky";
  if (normalized.includes("digestión")) return "teal";
  if (normalized.includes("evolución") || normalized.includes("como va")) return "orange";
  if (normalized.includes("mejora")) return "green";
  if (normalized.includes("menstrual") || normalized.includes("periodo") || normalized.includes("ciclo")) return "pink";
  if (normalized.includes("observación")) return "blue";
  return "blue";
}

function renderCheckInAnswerIcon(label: string) {
  const normalized = normalizeWeekday(label);
  if (normalized.includes("sueno")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
  if (normalized.includes("hambre")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h2v11"/><path d="M7 2v7"/><path d="M21 15V2l-3 3c-1 1-1.5 2.5-1.5 4S17 12 18 13l3 2z"/></svg>;
  if (normalized.includes("ansiedad")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2z"/><path d="M14.5 2a2.5 2.5 0 0 0-2.5 2.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2z"/></svg>;
  if (normalized.includes("estres")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 9-12h-9z"/></svg>;
  if (normalized.includes("comida")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16a2 2 0 0 0 0-4H4a2 2 0 0 0 0 4z"/><path d="M3 14h18"/><path d="M3 17a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3"/></svg>;
  if (normalized.includes("entrenamientos completados")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6v12M3 9v6M21 9v6M18 6v12M6 12h12"/></svg>;
  if (normalized.includes("cardio")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
  if (normalized.includes("agua")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69 5.66 8.82a8.04 8.04 0 0 0 0 11.36 8.04 8.04 0 0 0 11.36 0 8.04 8.04 0 0 0 0-11.36z"/></svg>;
  if (normalized.includes("digestión")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3c0 2 2 3 3 3 2 0 3 1 3 3 0 1.5-.5 2.5-1 3.5C18 14 18 17 16 19c-2 1.5-5 2-7 1-2-1-3.5-2.5-4-5-.5-2 0-4 1.5-5.5C8 8 8.5 6.5 9 5c.5-1.5 2-3 5-2z"/><circle cx="10" cy="12" r="1"/><circle cx="14" cy="14" r="1"/></svg>;
  if (normalized.includes("evolución") || normalized.includes("como va")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17 9 11l4 4 8-10"/><path d="M14 5h7v7"/></svg>;
  if (normalized.includes("mejora")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9z"/></svg>;
  if (normalized.includes("observación")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h14v13H8l-3 3Z" /><path d="M9 8h6M9 12h4" /></svg>;
}

function getCheckInMeasureTone(label: string) {
  const normalized = normalizeWeekday(label);
  if (normalized.includes("pliegue")) return "amber";
  if (normalized.includes("cintura") && normalized.includes("relajado")) return "coral";
  if (normalized.includes("cintura") && normalized.includes("apretado")) return "orange";
  if (normalized.includes("cintura")) return "orange";
  if (normalized.includes("cadera")) return "cyan";
  if (normalized.includes("brazo")) return "purple";
  if (normalized.includes("muslo")) return "violet";
  if (normalized.includes("cuádricep")) return "blue";
  if (normalized.includes("pecho")) return "blue";
  if (normalized.includes("glúteo") || normalized.includes("gluteo") || normalized.includes("nalga")) return "pink";
  return "orange";
}

function renderCheckInMeasureIcon(_label: string) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>;
}

type ProgressPhotoSlot = "FRONT" | "SIDE" | "BACK" | "FAVORITE_1" | "FAVORITE_2";

const PROGRESS_PHOTO_SLOTS: readonly ProgressPhotoSlot[] = ["FRONT", "SIDE", "BACK", "FAVORITE_1", "FAVORITE_2"];
const REQUIRED_PROGRESS_PHOTO_SLOTS: readonly ProgressPhotoSlot[] = ["FRONT", "SIDE", "BACK"];

function progressPhotoLabel(type: ProgressPhotoSlot | string) {
  switch (type) {
    case "FRONT":
      return "Frente";
    case "SIDE":
      return "Lado";
    case "BACK":
      return "Espalda";
    case "FAVORITE_1":
      return "Favorita 1";
    case "FAVORITE_2":
      return "Favorita 2";
    default:
      return String(type);
  }
}

function buildProgressPhotoFallback(type: ProgressPhotoSlot) {
  const label =
    type === "FRONT"
      ? "FRENTE"
      : type === "SIDE"
        ? "LADO"
        : type === "BACK"
          ? "ESPALDA"
          : type === "FAVORITE_1"
            ? "FAVORITA 1"
            : "FAVORITA 2";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="900" height="620" viewBox="0 0 900 620">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#111827"/>
          <stop offset="62%" stop-color="#1f2937"/>
          <stop offset="100%" stop-color="#e63946"/>
        </linearGradient>
      </defs>
      <rect width="900" height="620" rx="34" fill="url(#bg)"/>
      <circle cx="450" cy="220" r="82" fill="#f8fafc" opacity="0.9"/>
      <path d="M274 520c24-112 92-174 176-174s152 62 176 174" fill="#f8fafc" opacity="0.9"/>
      <text x="450" y="92" text-anchor="middle" font-family="Arial, sans-serif" font-size="34" font-weight="700" letter-spacing="8" fill="#ffb4bc">${label}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function resolveProgressPhotoUrl(type: ProgressPhotoSlot, imageUrl: string) {
  if (!imageUrl || imageUrl.startsWith("demo://")) return buildProgressPhotoFallback(type);
  if (/^(https?:|data:|blob:)/i.test(imageUrl)) return imageUrl;
  const baseUrl = String(apiBaseUrl || "").replace(/\/$/, "");
  const path = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
  return `${baseUrl}${path}`;
}

export function ClientDetailPage() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [data, setData] = useState<ClientHistoryResponse | null>(null);
  const [activeTab, setActiveTab] = useState<CoachClientTab>(
    requestedTab === "checkin" || requestedTab === "routine" || requestedTab === "progress" || requestedTab === "summary" ? requestedTab : "summary",
  );

  async function loadHistory() {
    if (!clientId) return;
    const response = await getClientHistory(clientId);
    setData(response);
  }

  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState<string | null>(null);

  async function handleValidateCheckIn(checkInId: string) {
    if (!clientId) return;
    setValidateError(null);
    setValidating(true);
    try {
      const updated = await validateCheckIn(clientId, checkInId);
      setData((current) => {
        if (!current) return current;
        return {
          ...current,
          checkIns: current.checkIns.map((c) =>
            c.id === checkInId
              ? { ...c, validatedAt: updated.validatedAt, validatedById: updated.validatedById }
              : c,
          ),
        };
      });
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message || err?.message || "No se pudo validar el check-in. Intenta nuevamente.";
      setValidateError(String(serverMessage));
    } finally {
      setValidating(false);
    }
  }

  useEffect(() => {
    loadHistory();
  }, [clientId]);

  useEffect(() => {
    if (requestedTab === "checkin" || requestedTab === "routine" || requestedTab === "progress" || requestedTab === "summary") {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  const sortedCheckIns = useMemo(
    () =>
      [...(data?.checkIns ?? [])].sort(
        (left, right) => new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime(),
      ),
    [data?.checkIns],
  );

  const selectedTrainingDays = useMemo(
    () => extractTrainingDays(data?.initialAssessment?.trainingRoutine || ""),
    [data?.initialAssessment?.trainingRoutine],
  );

  const latestCheckIn = sortedCheckIns[0] ?? null;

  const [progressCheckInId, setProgressCheckInId] = useState<string | null>(null);
  const [weekMenuOpen, setWeekMenuOpen] = useState(false);
  const progressCheckIn = useMemo(() => {
    if (!sortedCheckIns.length) return null;
    if (progressCheckInId) {
      return sortedCheckIns.find((checkIn) => checkIn.id === progressCheckInId) ?? sortedCheckIns[0];
    }
    return sortedCheckIns[0];
  }, [sortedCheckIns, progressCheckInId]);

  const progressRows = useMemo(
    () => (progressCheckIn ? buildTrackingRowsFromCheckIn(progressCheckIn, selectedTrainingDays) : []),
    [progressCheckIn, selectedTrainingDays],
  );
  const hasProgressRows = progressRows.length > 0;
  const progressWaterAverage = averageTrackingField(progressRows, "waterLiters");
  const progressSleepAverage = averageTrackingField(progressRows, "sleepHours");
  const progressStepsAverage = averageTrackingField(progressRows, "steps");
  const progressWeightAverage = averageTrackingField(progressRows, "weightKg");
  const progressRecoveryAverage = averageTrackingField(progressRows, "recoveryScore");
  const progressVariation = calculateWeightVariation(progressRows);
  const progressWeekStart = progressCheckIn?.weekStart.slice(0, 10) ?? "—";

  const savedAnswers = latestCheckIn ? buildSavedCheckInAnswers(latestCheckIn) : [];
  const measurements = progressCheckIn?.bodyMeasurements?.[0];
  const summaryActivity = [
    latestCheckIn ? `Check-in enviado el ${formatShortDashboardDate(latestCheckIn.submittedAt)}` : "Sin check-ins registrados",
    measurements ? "Medidas corporales actualizadas" : "Sin medidas corporales registradas",
  ];

  const assessmentSummaryItems = useMemo(
    () => buildAssessmentSummaryItems(data?.initialAssessment ?? null),
    [data?.initialAssessment],
  );
  const fullName = `${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`.trim();
  const initials = [data?.client.firstName?.[0], data?.client.lastName?.[0]].filter(Boolean).join("").toUpperCase() || "JC";

  return (
    <section className="page client-detail-page coach-client-detail-page adminlte-inner-page">
      <div className="coach-detail-top">
      <header className="page-header dashboard-hero client-hero client-detail-hero content-header-card adminlte-content-header coach-detail-profile">
        <div className="coach-detail-avatar coach-detail-avatar--photo">
          {data?.client.profilePhotoUrl ? (
            <img src={`${apiBaseUrl}${data.client.profilePhotoUrl}`} alt={fullName || "Cliente"} />
          ) : (
            <span>{initials}</span>
          )}
          <b aria-hidden="true">✓</b>
        </div>
        <div className="client-detail-hero-copy">
          <p className="eyebrow">Ficha del cliente</p>
          <h2>{fullName || "Cliente"}</h2>
          <div className="coach-detail-objective">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><path d="M12 3v3M21 12h-3" /></svg>
            {data?.client.objective || "Bajar peso y definir músculo"}
          </div>
          <p className="muted dashboard-hero-copy">
            {data?.client.objective || "Bienvenido al portal del cliente"}.{" "}
            Desde aquí puedes revisar su progreso, sus check-ins y la rutina diseñada.
          </p>
        </div>
        <div className="content-header-meta">
          <div className="content-header-chip">
            <span>Estado</span>
            <strong>{statusLabel[data?.client.latestStatus ?? "NEW"] ?? "Nuevo"}</strong>
          </div>
          <div className="content-header-chip">
            <span>Check-ins</span>
            <strong>{data?.checkIns.length ?? 0}</strong>
          </div>
        </div>
      </header>
      <aside className="coach-detail-actions">
        <button type="button" className="coach-detail-action coach-detail-action--primary">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
          Editar cliente
        </button>
        <button type="button" className="coach-detail-action" onClick={() => setActiveTab("progress")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7 15 4-4 3 3 5-7" /></svg>
          Ver procesos
        </button>
        <button type="button" className="coach-detail-action coach-detail-action--danger">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 14H6L5 6" /></svg>
          Eliminar
        </button>
      </aside>
      </div>

      <div className="portal-tabs adminlte-tabs-nav coach-detail-tabs">
        <button type="button" className={`portal-tab coach-detail-tab ${activeTab === "summary" ? "active" : ""}`} onClick={() => setActiveTab("summary")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11 12 3l9 8" /><path d="M5 10v10h14V10" /></svg>
          Resumen
        </button>
        <button type="button" className={`portal-tab coach-detail-tab ${activeTab === "progress" ? "active" : ""}`} onClick={() => setActiveTab("progress")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19V5" /><path d="M10 19V9" /><path d="M16 19v-6" /><path d="M22 19H2" /></svg>
          Progreso
        </button>
        <button type="button" className={`portal-tab coach-detail-tab ${activeTab === "checkin" ? "active" : ""}`} onClick={() => setActiveTab("checkin")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="18" rx="2" /><path d="M8 2v4M16 2v4M8 14l3 3 5-6" /></svg>
          Check-in
        </button>
        <button type="button" className={`portal-tab coach-detail-tab ${activeTab === "routine" ? "active" : ""}`} onClick={() => setActiveTab("routine")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6v12M18 6v12M3 9v6M21 9v6M6 12h12" /></svg>
          Rutina
        </button>
      </div>

      {activeTab === "summary" && (
        <div className="cdash-page coach-client-summary-dashboard">
          <div className="cdash-header">
            <div className="cdash-greeting">
              <h1 className="cdash-title">Dashboard del cliente</h1>
              <p className="cdash-subtitle">Resumen real del progreso y check-ins de {fullName || "este cliente"}.</p>
            </div>
            <div className="cdash-date-picker">
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></svg>
              <span>{latestCheckIn ? `Semana ${formatShortDashboardDate(latestCheckIn.weekStart)}` : "Sin semana activa"}</span>
            </div>
          </div>

          <div className="cdash-kpi-grid">
            {[
              { label: "Check-ins", value: String(data?.checkIns.length ?? 0), delta: latestCheckIn ? `Último: ${formatShortDashboardDate(latestCheckIn.submittedAt)}` : "Sin registros", tone: "orange", icon: "clipboard" },
              { label: "Peso promedio", value: progressWeightAverage === "-" ? "-" : `${progressWeightAverage} kg`, delta: progressVariation === "-" ? "Sin variación" : `${progressVariation} kg vs. semana`, tone: "amber", icon: "scale" },
              { label: "Agua promedio", value: progressWaterAverage === "-" ? "-" : `${progressWaterAverage} L`, delta: "Desde último check-in", tone: "teal", icon: "drop" },
              { label: "Sueño promedio", value: progressSleepAverage === "-" ? "-" : `${progressSleepAverage} h`, delta: "Desde último check-in", tone: "coral", icon: "moon" },
            ].map((card) => (
              <article key={card.label} className={`cdash-kpi cdash-kpi--${card.tone}`}>
                <div className="cdash-kpi-top">
                  <span className={`cdash-kpi-icon db2-kpi-icon--${card.tone}`} aria-hidden="true">
                    {card.icon === "clipboard" ? (
                      <svg viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 5a3 3 0 0 1 6 0" /><path d="m9 14 2 2 4-5" /></svg>
                    ) : card.icon === "scale" ? (
                      <svg viewBox="0 0 24 24"><rect x="5" y="4" width="14" height="16" rx="3" /><path d="M9 10a3 3 0 0 1 6 0" /></svg>
                    ) : card.icon === "drop" ? (
                      <svg viewBox="0 0 24 24"><path d="M12 3s6 6.2 6 11a6 6 0 0 1-12 0c0-4.8 6-11 6-11z" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24"><path d="M20 15.2A8 8 0 0 1 8.8 4 7 7 0 1 0 20 15.2z" /></svg>
                    )}
                  </span>
                  <div>
                    <p className="cdash-kpi-label">{card.label}</p>
                    <p className="cdash-kpi-value">{card.value}</p>
                  </div>
                </div>
                <p className="cdash-kpi-delta">{card.delta}</p>
              </article>
            ))}
          </div>

          <div className="cdash-mid-row">
            <section className="cdash-panel">
              <div className="cdash-panel-head">
                <div>
                  <h3 className="cdash-panel-title">Actividad reciente</h3>
                  <p className="cdash-panel-copy">Eventos obtenidos del historial del cliente.</p>
                </div>
              </div>
              <div className="cdash-list cdash-list--recommendations">
                {summaryActivity.map((item, index) => (
                  <div key={`${item}-${index}`} className="cdash-list-row">
                    <span className={`cdash-row-icon cdash-row-icon--${index}`} aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M12 6v6l4 2" /><circle cx="12" cy="12" r="9" /></svg>
                    </span>
                    <strong>{item}</strong>
                    <span>{index === 0 && latestCheckIn ? formatShortDashboardDate(latestCheckIn.submittedAt) : "Actual"}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="cdash-panel cdash-panel--full">
            <div className="cdash-panel-head">
              <div>
                <h3 className="cdash-panel-title">Resumen semanal</h3>
                <p className="cdash-panel-copy">Datos del último seguimiento semanal del cliente.</p>
              </div>
              <button className="cdash-btn-primary" type="button" onClick={() => setActiveTab("progress")}>Ver progreso</button>
            </div>
            <div className="cdash-table-wrap">
              <table className="cdash-table">
                <thead>
                  <tr>
                    <th>Objetivo</th>
                    <th>Agua</th>
                    <th>Sueño</th>
                    <th>Pasos</th>
                    <th>Peso</th>
                    <th>Fotos</th>
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="cdash-td-label">{data?.client.objective || "Sin objetivo"}</td>
                    <td>{progressWaterAverage === "-" ? "-" : `${progressWaterAverage} L`}</td>
                    <td>{progressSleepAverage === "-" ? "-" : `${progressSleepAverage} h`}</td>
                    <td>{progressStepsAverage === "-" ? "-" : Number(progressStepsAverage).toLocaleString("es-PE")}</td>
                    <td>{progressWeightAverage === "-" ? "-" : `${progressWeightAverage} kg`}</td>
                    <td>{latestCheckIn?.progressPhotos.length ?? 0}</td>
                    <td><span className="cdash-status-pill">{statusLabel[data?.client.latestStatus ?? "NEW"] ?? "Nuevo"}</span></td>
                    <td><button className="cdash-row-action" type="button" aria-label="Ver check-in" onClick={() => setActiveTab("checkin")}>›</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeTab === "progress" && (
        <div className="progress-page">
          <section className="progress-controls">
            <div className={`progress-week-card${weekMenuOpen ? " progress-week-card--open" : ""}`}>
              <button
                type="button"
                className="progress-week-trigger"
                onClick={() => setWeekMenuOpen((open) => !open)}
                disabled={sortedCheckIns.length <= 1}
                aria-expanded={weekMenuOpen}
                aria-haspopup="listbox"
              >
                <span className="progress-control-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                </span>
                <div>
                  <span>Semana activa{sortedCheckIns.length > 1 ? ` · ${sortedCheckIns.length} guardadas` : ""}</span>
                  <strong>{progressWeekStart}</strong>
                </div>
                <span className="progress-chevron" aria-hidden="true">⌄</span>
              </button>
              {weekMenuOpen ? (
                <ul className="progress-week-menu" role="listbox" aria-label="Seleccionar semana">
                  {sortedCheckIns.map((checkIn) => {
                    const isActive = checkIn.id === progressCheckIn?.id;
                    return (
                      <li key={checkIn.id} role="option" aria-selected={isActive}>
                        <button
                          type="button"
                          className={`progress-week-option${isActive ? " progress-week-option--active" : ""}`}
                          onClick={() => {
                            setProgressCheckInId(checkIn.id);
                            setWeekMenuOpen(false);
                          }}
                        >
                          <strong>{checkIn.weekStart.slice(0, 10)}</strong>
                          <span>Enviado {formatShortDashboardDate(checkIn.submittedAt)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
            <div className="progress-readonly-card">
              <span className="progress-control-icon progress-control-icon--blue" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="6" y="10" width="12" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
              </span>
              <div>
                <strong>Vista de solo lectura</strong>
                <p>Esta información se sincroniza automáticamente desde el último seguimiento semanal del cliente.</p>
              </div>
            </div>

            {progressCheckIn ? (
              <div className="checkin-validate-card">
                {progressCheckIn.validatedAt ? (
                  <>
                    <span className="checkin-validate-badge checkin-validate-badge--ok" aria-hidden="true">✓</span>
                    <div>
                      <strong>Check-in validado</strong>
                      <p>Validado el {new Date(progressCheckIn.validatedAt).toLocaleDateString("es-PE")}.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="checkin-validate-badge" aria-hidden="true">!</span>
                    <div>
                      <strong>Pendiente de validación</strong>
                      <p>Revisa el check-in y validalo cuando este conforme.</p>
                      {validateError ? <small style={{ color: "#dc2626" }}>{validateError}</small> : null}
                    </div>
                    <button
                      type="button"
                      className="checkin-validate-btn"
                      onClick={() => handleValidateCheckIn(progressCheckIn.id)}
                      disabled={validating}
                    >
                      {validating ? "Validando..." : "Validar check-in"}
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </section>

          <section className="progress-metrics">
            {[
              { label: "Promedio de agua", value: progressWaterAverage === "-" ? "-" : `${progressWaterAverage} L`, delta: "vs. semana anterior", tone: "blue", icon: "drop" },
              { label: "Promedio de sueno", value: progressSleepAverage === "-" ? "-" : `${progressSleepAverage} h`, delta: "vs. semana anterior", tone: "purple", icon: "moon" },
              { label: "Promedio de pasos", value: progressStepsAverage === "-" ? "-" : Number(progressStepsAverage).toLocaleString("es-PE"), delta: "vs. semana anterior", tone: "green", icon: "shoe" },
              { label: "Peso promedio", value: progressWeightAverage === "-" ? "-" : `${progressWeightAverage} kg`, delta: "vs. semana anterior", tone: "orange", icon: "scale" },
              { label: "Variación de peso", value: progressVariation === "-" ? "-" : `${progressVariation} kg`, delta: "vs. semana anterior", tone: "amber", icon: "trend" },
            ].map((metric) => (
              <article key={metric.label} className={`progress-metric progress-metric--${metric.tone}`}>
                <span className="progress-metric-icon" aria-hidden="true">
                  {metric.icon === "drop" ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.5c-.4 0-.8.2-1 .5C9.6 4.7 5 10.4 5 14.5c0 3.9 3.1 7 7 7s7-3.1 7-7c0-4.1-4.6-9.8-6-11.5-.2-.3-.6-.5-1-.5z" /></svg>
                  ) : metric.icon === "moon" ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 15.3c-.2-.3-.6-.4-.9-.3-1 .3-2 .4-3 .3-3.7-.4-6.7-3.4-7.1-7.1-.1-1 0-2 .3-3 .1-.3 0-.7-.3-.9-.3-.2-.7-.2-1 0C5.5 5.9 3.5 9.2 3.5 13c0 5.2 4.3 9.5 9.5 9.5 3.8 0 7.1-2 8.7-5 .2-.3.2-.7-.2-1z" /></svg>
                  ) : metric.icon === "shoe" ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 16.5c0 1.4 1.1 2.5 2.5 2.5h13c1.9 0 3.5-1.6 3.5-3.5 0-.8-.3-1.5-.8-2.1l-6.7-7c-.5-.5-1.2-.8-1.9-.8-1 0-1.9.6-2.3 1.5-.8 1.7-2 4.1-3.6 5.4-.9.7-2.1 1.3-3.1 1.6-.4.1-.6.5-.6.9v1.5z" /></svg>
                  ) : metric.icon === "scale" ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 9a4 4 0 0 1 8 0" /><line x1="12" y1="9" x2="10.5" y2="11" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-9" /><path d="M14 6h7v7" /></svg>
                  )}
                </span>
                <div>
                  <p>{metric.label}</p>
                  <strong>{metric.value}</strong>
                  <span>↑ {metric.delta}</span>
                </div>
              </article>
            ))}
          </section>

          <section className="progress-board">
            <div className="progress-table-panel">
              <div className="progress-panel-head">
                <span className="progress-section-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                </span>
                <div>
                  <h3>Registro diario de la semana</h3>
                  <p>Datos consolidados desde el último seguimiento semanal del cliente.</p>
                </div>
              </div>
              <div className="progress-table-wrap">
                <table className="progress-table">
                  <thead>
                    <tr>
                      <th>Día</th>
                      <th>Agua (L)</th>
                      <th>Horas de sueno</th>
                      <th>Pasos diarios</th>
                      <th>Recuperación (1-10)</th>
                      <th>Peso (kg)</th>
                      <th>Tipo de dieta</th>
                      <th>Músculo entrenado</th>
                      <th>Rendimiento</th>
                      <th>Cardio (min)</th>
                      <th>Glucosa (mg/dL)</th>
                      <th>Reflexiones del día</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasProgressRows ? (
                      progressRows.map((row, index) => (
                        <tr key={row.date}>
                          <td>
                            <div className="progress-day">
                              <span className={`progress-day-icon progress-day-icon--${index % 4}`} aria-hidden="true">
                                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M4.9 4.9 7 7M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1" /></svg>
                              </span>
                              <div>
                                <strong>{row.label}</strong>
                                <span>{formatShortDashboardDate(row.date)}</span>
                              </div>
                            </div>
                          </td>
                          <td>{row.waterLiters || "-"}</td>
                          <td>{row.sleepHours || "-"}</td>
                          <td>{row.steps || "-"}</td>
                          <td>{row.recoveryScore || "-"}</td>
                          <td>{row.weightKg || "-"}</td>
                          <td><span className="progress-diet">{row.dietType || "-"}</span></td>
                          <td>{formatMuscleTrainedLabel(row.trainedMuscle) || "-"}</td>
                          <td><span className="progress-performance">{row.trainingPerformance || "-"}</span></td>
                          <td>{row.cardio || "-"}</td>
                          <td>{row.bloodGlucose || "-"}</td>
                          <td className="progress-reflection">{row.dayReflections || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={12} className="progress-empty-cell">
                          Aún no hay registros guardados para este cliente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {hasProgressRows ? (
                    <tfoot>
                      <tr>
                        <td>Promedio</td>
                        <td>{progressWaterAverage}</td>
                        <td>{progressSleepAverage}</td>
                        <td>{progressStepsAverage === "-" ? "-" : Number(progressStepsAverage).toLocaleString("es-PE")}</td>
                        <td>{progressRecoveryAverage}</td>
                        <td>{progressWeightAverage}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>Variación de peso: {progressVariation === "-" ? "-" : `${progressVariation} kg`}</td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            </div>
          </section>

          <section className="progress-measures-panel">
            <div className="progress-panel-head">
              <span className="progress-section-icon progress-section-icon--measures" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="m21.3 15.3-6 6a1 1 0 0 1-1.4 0l-12-12a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0l12 12a1 1 0 0 1 0 1.4Z" /><path d="M7 7h.01M10 10h.01M13 13h.01M16 16h.01" /></svg>
              </span>
              <div>
                <h3>Medidas corporales del check-in</h3>
                <p>Medidas solicitadas en el seguimiento semanal para comparar cambios reales.</p>
              </div>
            </div>
            <div className="progress-measures-table-wrap">
              <table className="progress-measures-table">
                <thead>
                  <tr>
                    <th>Semana</th>
                    <th>Cintura</th>
                    <th>Cadera</th>
                    <th>Pecho</th>
                    <th>Brazo derecho</th>
                    <th>Brazo izquierdo</th>
                    <th>Muslo derecho</th>
                    <th>Muslo izquierdo</th>
                  </tr>
                </thead>
                <tbody>
                  {measurements ? (
                    <tr>
                      <td><strong>{progressCheckIn?.weekStart.slice(0, 10) ?? "-"}</strong></td>
                      <td>{measurements.waistCm ? `${measurements.waistCm} cm` : "-"}</td>
                      <td>{measurements.hipsCm ? `${measurements.hipsCm} cm` : "-"}</td>
                      <td>{measurements.chestCm ? `${measurements.chestCm} cm` : "-"}</td>
                      <td>{measurements.rightArmCm ? `${measurements.rightArmCm} cm` : "-"}</td>
                      <td>{measurements.leftArmCm ? `${measurements.leftArmCm} cm` : "-"}</td>
                      <td>{measurements.rightThighCm ? `${measurements.rightThighCm} cm` : "-"}</td>
                      <td>{measurements.leftThighCm ? `${measurements.leftThighCm} cm` : "-"}</td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan={8} className="progress-empty-cell">
                        No hay medidas corporales registradas para este check-in.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeTab === "checkin" && (
        <div className="checkin-shell">
          {latestCheckIn ? (
            <article className="checkin-detail-page">
              <div className="checkin-print-letterhead" aria-hidden="true">
                <img src="/logos/jc-logo.png" alt="JC Fitness" />
                <strong>{fullName}</strong>
              </div>
              <section className="checkin-detail-hero">
                <div>
                  <h1>Detalle del check-in semanal</h1>
                  <p>Revisa las respuestas, fotos y resumen general de la semana del cliente.</p>
                </div>
                <div className="checkin-detail-hero-meta">
                  <span><b>Semana</b><strong>{latestCheckIn.weekStart.slice(0, 10)}</strong></span>
                  <span><b>Estado:</b><strong>Revisado</strong></span>
                  <span><b>Coach:</b><strong>{fullName ? "Tú" : "Coach"}</strong></span>
                </div>
              </section>

              <section className="checkin-detail-metrics">
                {[
                  { label: "Peso promedio", value: averageTrackingField(progressRows, "weightKg") === "-" ? "—" : `${averageTrackingField(progressRows, "weightKg")} kg`, tone: "blue", icon: "scale" },
                  { label: "Promedio de sueño", value: averageTrackingField(progressRows, "sleepHours") === "-" ? "—" : `${averageTrackingField(progressRows, "sleepHours")} h`, tone: "teal", icon: "moon" },
                  { label: "Promedio de agua", value: averageTrackingField(progressRows, "waterLiters") === "-" ? "—" : `${averageTrackingField(progressRows, "waterLiters")} L`, tone: "orange", icon: "drop" },
                  { label: "Pasos promedio", value: averageTrackingField(progressRows, "steps") === "-" ? "—" : Number(averageTrackingField(progressRows, "steps")).toLocaleString("es-PE"), tone: "red", icon: "walk" },
                ].map((metric) => (
                  <article key={metric.label} className={`checkin-detail-metric checkin-detail-metric--${metric.tone}`}>
                    <span aria-hidden="true">
                      {metric.icon === "scale" ? (
                        <svg viewBox="0 0 24 24"><rect x="5" y="4" width="14" height="16" rx="3" /><path d="M9 10a3 3 0 0 1 6 0" /></svg>
                      ) : metric.icon === "moon" ? (
                        <svg viewBox="0 0 24 24"><path d="M20 15.5A8 8 0 1 1 8.5 4 6.5 6.5 0 0 0 20 15.5z" /></svg>
                      ) : metric.icon === "drop" ? (
                        <svg viewBox="0 0 24 24"><path d="M12 3c4 5.5 6 9 6 12a6 6 0 0 1-12 0c0-3 2-6.5 6-12z" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24"><path d="M13 4 7 14h5l-1 6 6-10h-5z" /></svg>
                      )}
                    </span>
                    <div>
                      <p>{metric.label}</p>
                      <strong>{metric.value}</strong>
                    </div>
                  </article>
                ))}
              </section>

              <section className="checkin-detail-grid">
                <article className="checkin-detail-card checkin-detail-photos">
                  <h2>
                    <span className="checkin-detail-heading-icon checkin-detail-heading-icon--photos" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M7 7h2l1.5-2h3L15 7h2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" /><circle cx="12" cy="13" r="3" /></svg>
                    </span>
                    1. Fotos registradas
                  </h2>
                  <div className="checkin-detail-photo-grid">
                    {PROGRESS_PHOTO_SLOTS.filter(
                      (slot) =>
                        REQUIRED_PROGRESS_PHOTO_SLOTS.includes(slot) ||
                        latestCheckIn.progressPhotos.some((item) => item.type === slot),
                    ).map((type) => {
                      const photo = latestCheckIn.progressPhotos.find((item) => item.type === type);
                      return (
                        <div key={type} className="checkin-detail-photo">
                          <strong>{progressPhotoLabel(type)}</strong>
                          <img
                            src={photo ? resolveProgressPhotoUrl(photo.type, photo.imageUrl) : buildProgressPhotoFallback(type)}
                            alt={`Foto de progreso ${type}`}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = buildProgressPhotoFallback(type);
                            }}
                          />
                          <span>{photo ? "Registrada" : "Sin foto"}</span>
                        </div>
                      );
                    })}
                  </div>
                </article>

                <article className="checkin-detail-card checkin-detail-daily">
                  <h2>
                    <span className="checkin-detail-heading-icon checkin-detail-heading-icon--daily" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16M9 15l2 2 4-5" /></svg>
                    </span>
                    2. Respuestas diarias registradas
                  </h2>
                  <div className="checkin-detail-table-wrap">
                    <table className="checkin-detail-table">
                      <thead>
                        <tr>
                          <th>Día</th>
                          <th>Agua (L)</th>
                          <th>Horas de sueño</th>
                          <th>Pasos</th>
                          <th>Recuperación 1-10</th>
                          <th>Peso (kg)</th>
                          <th>Tipo de dieta</th>
                          <th>Músculo entrenado</th>
                          <th>Rendimiento</th>
                          <th>Cardio</th>
                          <th>Glucosa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progressRows.map((row) => (
                          <tr key={row.date}>
                            <td><strong>{row.label}</strong></td>
                            <td>{row.waterLiters || "—"}</td>
                            <td>{row.sleepHours || "—"}</td>
                            <td>{row.steps || "—"}</td>
                            <td>{row.recoveryScore || "—"}</td>
                            <td>{row.weightKg || "—"}</td>
                            <td>{row.dietType || "—"}</td>
                            <td>{formatMuscleTrainedLabel(row.trainedMuscle) || "—"}</td>
                            <td>{row.trainingPerformance || "—"}</td>
                            <td>{row.cardio || "—"}</td>
                            <td>{row.bloodGlucose || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </article>
              </section>

              <section className="checkin-detail-card checkin-detail-measures">
                <h2>
                  <span className="checkin-detail-heading-icon checkin-detail-heading-icon--measures" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="m21.3 15.3-6 6a1 1 0 0 1-1.4 0l-12-12a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0l12 12a1 1 0 0 1 0 1.4Z" /></svg>
                  </span>
                  3. Medidas registradas
                </h2>
                {measurements ? (
                  <div className="checkin-detail-measure-grid">
                    {([
                      ["Cintura", measurements.waistCm, "cm"],
                      ["Cadera", measurements.hipsCm, "cm"],
                      ["Pecho", measurements.chestCm, "cm"],
                      ["Brazo derecho", measurements.rightArmCm, "cm"],
                      ["Brazo izquierdo", measurements.leftArmCm, "cm"],
                      ["Muslo derecho", measurements.rightThighCm, "cm"],
                      ["Muslo izquierdo", measurements.leftThighCm, "cm"],
                      ["Glúteo", measurements.glutesCm, "cm"],
                    ] as Array<[string, number | undefined, string]>)
                      .filter(([, value]) => value !== undefined && value !== null)
                      .map(([label, value, unit]) => (
                        <article key={label} className={`checkin-detail-measure checkin-detail-measure--${getCheckInMeasureTone(label)}`}>
                          <span aria-hidden="true">{renderCheckInMeasureIcon(label)}</span>
                          <div>
                            <strong>{label}</strong>
                            <p>{value} {unit}</p>
                          </div>
                        </article>
                      ))}
                  </div>
                ) : (
                  <p className="tracking-helper-text">No hay medidas registradas para esta semana.</p>
                )}
              </section>

              <section className="checkin-detail-card checkin-detail-answers">
                <h2>
                  <span className="checkin-detail-heading-icon checkin-detail-heading-icon--answers" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M5 4h14v13H8l-3 3Z" /><path d="M9 8h6M9 12h4" /></svg>
                  </span>
                  4. Respuestas generales
                </h2>
                <div className="checkin-detail-answer-grid">
                  {savedAnswers.length > 0 ? (
                    savedAnswers.map(([label, value]) => (
                      <article key={label} className={`checkin-detail-answer checkin-detail-answer--${getCheckInAnswerTone(label)}`}>
                        <span aria-hidden="true">{renderCheckInAnswerIcon(label)}</span>
                        <div>
                          <strong>{label}</strong>
                          <p>{value}</p>
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="tracking-helper-text">No hay respuestas de resumen registradas para esta semana.</p>
                  )}
                </div>
              </section>

              <footer className="checkin-detail-actions">
                <button
                  type="button"
                  onClick={() =>
                    printCheckInDocument(
                      buildCheckInPdfTitle(
                        fullName || "Cliente",
                        getCheckInWeekNumber(data?.checkIns ?? [], latestCheckIn.id),
                      ),
                    )
                  }
                >
                  Imprimir / guardar PDF
                </button>
              </footer>
            </article>
          ) : (
            <div className="portal-empty">
              <span>Sin datos</span>
              <p>El cliente aún no ha registrado un seguimiento semanal.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "routine" && (
        data?.initialAssessment && assessmentSummaryItems.length > 0 ? (
          <div className="routine-summary-page">
            <section className="routine-summary-hero">
              <div>
                <h1>Rutina y registro inicial</h1>
                <p>
                  Resumen organizado de la experiencia, hábitos, alimentación y antecedentes del cliente para que diseñes su plan.
                </p>
              </div>
            </section>

            <section className="routine-summary-grid">
              {assessmentSummaryItems.map((item, index) => (
                <article key={item.id} className={`routine-summary-card routine-summary-card--${item.tone}`}>
                  <span className="routine-summary-card-icon" aria-hidden="true">
                    {renderAssessmentSummaryIcon(item.icon)}
                  </span>
                  <div>
                    <h2>{index + 1}. {item.title}</h2>
                    {item.bullets.length > 0 ? (
                      <ul>
                        {item.bullets.map((line) => (
                          <li key={`${item.id}-${line}`}>{line}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="routine-summary-empty">Sin respuestas registradas.</p>
                    )}
                  </div>
                </article>
              ))}
            </section>
          </div>
        ) : (
          <div className="portal-empty">
            <span>Sin registro inicial</span>
            <p>El cliente aún no ha completado su registro inicial.</p>
          </div>
        )
      )}
    </section>
  );
}
